import type {
  Puzzle,
  SolveResult,
  SolveStep,
  Technique,
  PuzzleClues,
  FullSettleResult,
  SolutionAnalysisOptions,
  SolutionAnalysisResult,
} from '../types.js';
import { CellValue } from '../types.js';
import { solveLine } from './line-solver.js';
import { isLineValid } from './consistency.js';
import { fastAnalyzeSolutions } from './fast-solver.js';

/**
 * Solve a nonogram puzzle using iterative line-solving with
 * sweep-based tracking, and backtracking as fallback.
 *
 * Sweeps alternate: h-sweep (all rows), v-sweep (all columns).
 * The sweepCount in the result tracks how many sweeps were needed.
 */
export function solveGrid(puzzle: Puzzle): SolveResult {
  const { rows, cols, clues } = puzzle;
  const grid = createGrid(rows, cols);
  const steps: SolveStep[] = [];
  const techniquesUsed = new Set<Technique>();

  const result = propagateSweeps(grid, clues, rows, cols, steps, techniquesUsed);

  if (result.outcome === 'solved') {
    return {
      solved: true,
      grid,
      steps,
      techniquesUsed,
      usedBacktracking: false,
      sweepCount: result.sweepCount,
      requiresBacktracking: false,
    };
  }

  if (result.outcome === 'contradiction') {
    return {
      solved: false,
      grid,
      steps,
      techniquesUsed,
      usedBacktracking: false,
      sweepCount: result.sweepCount,
      requiresBacktracking: false,
    };
  }

  // Stuck — try backtracking
  const lineSweepCount = result.sweepCount;
  const btResult = backtrack(grid, clues, rows, cols, steps, techniquesUsed);
  techniquesUsed.add('backtracking');

  return {
    solved: btResult,
    grid,
    steps,
    techniquesUsed,
    usedBacktracking: true,
    sweepCount: lineSweepCount,
    requiresBacktracking: true,
  };
}

/**
 * FullSettle: keep alternating h-sweep and v-sweep until no more progress.
 * Returns the grid state, remaining unknowns, and sweep count.
 * Does NOT use backtracking — pure line-logic only.
 *
 * The optional `initialDirty` narrows the starting dirty set. Callers that
 * know only a handful of lines could have changed (e.g. a warm-started
 * trial from adaptPuzzle after a single-cell flip) pass those indices so
 * propagation doesn't re-sweep the already-settled lines.
 */
export function fullSettle(
  initialGrid: CellValue[][],
  clues: PuzzleClues,
  initialDirty?: { rows?: number[]; cols?: number[] },
): FullSettleResult {
  const rows = initialGrid.length;
  const cols = rows > 0 ? initialGrid[0].length : 0;
  const grid = cloneGrid(initialGrid);
  const steps: SolveStep[] = [];
  const techniquesUsed = new Set<Technique>();

  const result = propagateSweeps(
    grid,
    clues,
    rows,
    cols,
    steps,
    techniquesUsed,
    initialDirty,
  );

  let remainingUnknowns = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (grid[r][c] === CellValue.Unknown) remainingUnknowns++;
    }
  }

  return {
    grid,
    remainingUnknowns,
    sweepCount: result.sweepCount,
  };
}

/**
 * Analyze solutions up to `limit`, optionally under a branch-state budget.
 * Does not modify the passed grid.
 */
export function analyzeSolutionsGrid(
  puzzle: Puzzle,
  options: SolutionAnalysisOptions = {}
): SolutionAnalysisResult {
  // Fast path: bitboard solver for grids ≤ 32×32
  const fast = fastAnalyzeSolutions(puzzle, options);
  if (fast !== null) return fast;

  // Fallback for oversized grids (> 32 in either dimension)
  const { rows, cols, clues } = puzzle;
  const limit = options.limit ?? 2;
  const grid = createGrid(rows, cols);
  const steps: SolveStep[] = [];
  const techniques = new Set<Technique>();

  const result = propagate(grid, clues, rows, cols, steps, techniques);

  if (result === 'contradiction') {
    return { count: 0, complete: true, aborted: false, exploredStates: 0 };
  }
  if (result === 'solved') {
    return { count: 1, complete: true, aborted: false, exploredStates: 0 };
  }

  const search: SolutionSearchState = {
    limit,
    maxStates: options.maxStates ?? Number.POSITIVE_INFINITY,
    exploredStates: 0,
    aborted: false,
  };

  const count = countSolutionsBacktrack(grid, clues, rows, cols, search);

  return {
    count,
    complete: !search.aborted,
    aborted: search.aborted,
    exploredStates: search.exploredStates,
  };
}

/**
 * Solve grid but count solutions up to `limit`.
 * Returns the exact count. Does not modify the passed grid.
 */
export function countSolutionsGrid(
  puzzle: Puzzle,
  limit: number
): number {
  return analyzeSolutionsGrid(puzzle, { limit }).count;
}

// --- Internal ---

function createGrid(rows: number, cols: number): CellValue[][] {
  return Array.from({ length: rows }, () =>
    new Array<CellValue>(cols).fill(CellValue.Unknown)
  );
}

function cloneGrid(grid: CellValue[][]): CellValue[][] {
  return grid.map((row) => [...row]);
}

type PropagateOutcome = 'solved' | 'stuck' | 'contradiction';

interface SweepResult {
  outcome: PropagateOutcome;
  sweepCount: number;
}

/**
 * Propagate using alternating sweeps: h-sweep (rows) then v-sweep (columns).
 * Each complete pass over all rows or all columns counts as one sweep.
 * Uses dirty-line tracking within each sweep for efficiency, but still
 * counts each full pass as one sweep.
 */
function propagateSweeps(
  grid: CellValue[][],
  clues: { rows: number[][]; cols: number[][] },
  rows: number,
  cols: number,
  steps: SolveStep[],
  techniquesUsed: Set<Technique>,
  initialDirty?: { rows?: number[]; cols?: number[] },
): SweepResult {
  let sweepCount = 0;

  const dirtyRows = new Uint8Array(rows);
  const dirtyCols = new Uint8Array(cols);
  if (initialDirty) {
    if (initialDirty.rows) for (const r of initialDirty.rows) dirtyRows[r] = 1;
    if (initialDirty.cols) for (const c of initialDirty.cols) dirtyCols[c] = 1;
  } else {
    dirtyRows.fill(1);
    dirtyCols.fill(1);
  }

  // Alternate between row sweeps and column sweeps
  // Start with h-sweep (rows), then v-sweep (columns), etc.
  let doRows = true;

  // Continue while there are dirty lines in either direction
  while (hasDirty(dirtyRows) || hasDirty(dirtyCols)) {
    if (doRows && hasDirty(dirtyRows)) {
      sweepCount++;
      // Process all dirty rows in this sweep
      // Rows dirtied during this sweep are processed in the NEXT row sweep
      const currentDirty = new Uint8Array(dirtyRows);
      dirtyRows.fill(0);

      for (let r = 0; r < rows; r++) {
        if (!currentDirty[r]) continue;

        const line = grid[r];
        const { deductions, technique } = solveLine(line, clues.rows[r]);

        if (deductions.length > 0) {
          if (technique) techniquesUsed.add(technique);

          const step: SolveStep = {
            technique: technique!,
            lineType: 'row',
            lineIndex: r,
            cellsDeduced: [],
          };

          for (const d of deductions) {
            if (grid[r][d.index] === CellValue.Unknown) {
              grid[r][d.index] = d.value;
              step.cellsDeduced.push({ row: r, col: d.index, value: d.value });
              dirtyCols[d.index] = 1;
            }
          }

          if (step.cellsDeduced.length > 0) {
            steps.push(step);
          }
        }

        // Check consistency
        if (!isLineValid(grid[r], clues.rows[r])) {
          return { outcome: 'contradiction', sweepCount };
        }
      }
    } else if (!doRows && hasDirty(dirtyCols)) {
      sweepCount++;
      // Process all dirty columns in this sweep
      const currentDirty = new Uint8Array(dirtyCols);
      dirtyCols.fill(0);

      for (let c = 0; c < cols; c++) {
        if (!currentDirty[c]) continue;

        const colLine: CellValue[] = new Array(rows);
        for (let r = 0; r < rows; r++) colLine[r] = grid[r][c];

        const { deductions, technique } = solveLine(colLine, clues.cols[c]);

        if (deductions.length > 0) {
          if (technique) techniquesUsed.add(technique);

          const step: SolveStep = {
            technique: technique!,
            lineType: 'col',
            lineIndex: c,
            cellsDeduced: [],
          };

          for (const d of deductions) {
            if (grid[d.index][c] === CellValue.Unknown) {
              grid[d.index][c] = d.value;
              step.cellsDeduced.push({ row: d.index, col: c, value: d.value });
              dirtyRows[d.index] = 1;
            }
          }

          if (step.cellsDeduced.length > 0) {
            steps.push(step);
          }
        }

        // Check consistency
        const colCheck: CellValue[] = new Array(rows);
        for (let r = 0; r < rows; r++) colCheck[r] = grid[r][c];
        if (!isLineValid(colCheck, clues.cols[c])) {
          return { outcome: 'contradiction', sweepCount };
        }
      }
    } else {
      // The current direction has no dirty lines, but the other does — switch
      doRows = !doRows;
      continue;
    }

    // Alternate direction
    doRows = !doRows;
  }

  // Check if solved
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (grid[r][c] === CellValue.Unknown) {
        return { outcome: 'stuck', sweepCount };
      }
    }
  }

  return { outcome: 'solved', sweepCount };
}

function hasDirty(flags: Uint8Array): boolean {
  for (let i = 0; i < flags.length; i++) {
    if (flags[i]) return true;
  }
  return false;
}

/**
 * Legacy propagate function used by backtracking and countSolutions.
 * Does not track sweeps — just solves as fast as possible.
 */
type PropagateResult = 'solved' | 'stuck' | 'contradiction';

function propagate(
  grid: CellValue[][],
  clues: { rows: number[][]; cols: number[][] },
  rows: number,
  cols: number,
  steps: SolveStep[],
  techniquesUsed: Set<Technique>,
): PropagateResult {
  const dirtyRows = new Uint8Array(rows).fill(1);
  const dirtyCols = new Uint8Array(cols).fill(1);

  let changed = true;
  while (changed) {
    changed = false;

    for (let r = 0; r < rows; r++) {
      if (!dirtyRows[r]) continue;
      dirtyRows[r] = 0;

      const line = grid[r];
      const { deductions, technique } = solveLine(line, clues.rows[r]);

      if (deductions.length > 0) {
        changed = true;
        if (technique) techniquesUsed.add(technique);

        const step: SolveStep = {
          technique: technique!,
          lineType: 'row',
          lineIndex: r,
          cellsDeduced: [],
        };

        for (const d of deductions) {
          if (grid[r][d.index] === CellValue.Unknown) {
            grid[r][d.index] = d.value;
            step.cellsDeduced.push({ row: r, col: d.index, value: d.value });
            dirtyCols[d.index] = 1;
          }
        }

        if (step.cellsDeduced.length > 0) {
          steps.push(step);
        }
      }

      if (!isLineValid(grid[r], clues.rows[r])) {
        return 'contradiction';
      }
    }

    for (let c = 0; c < cols; c++) {
      if (!dirtyCols[c]) continue;
      dirtyCols[c] = 0;

      const colLine: CellValue[] = new Array(rows);
      for (let r = 0; r < rows; r++) colLine[r] = grid[r][c];

      const { deductions, technique } = solveLine(colLine, clues.cols[c]);

      if (deductions.length > 0) {
        changed = true;
        if (technique) techniquesUsed.add(technique);

        const step: SolveStep = {
          technique: technique!,
          lineType: 'col',
          lineIndex: c,
          cellsDeduced: [],
        };

        for (const d of deductions) {
          if (grid[d.index][c] === CellValue.Unknown) {
            grid[d.index][c] = d.value;
            step.cellsDeduced.push({ row: d.index, col: c, value: d.value });
            dirtyRows[d.index] = 1;
          }
        }

        if (step.cellsDeduced.length > 0) {
          steps.push(step);
        }
      }

      const colCheck: CellValue[] = new Array(rows);
      for (let r = 0; r < rows; r++) colCheck[r] = grid[r][c];
      if (!isLineValid(colCheck, clues.cols[c])) {
        return 'contradiction';
      }
    }
  }

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (grid[r][c] === CellValue.Unknown) return 'stuck';
    }
  }

  return 'solved';
}

/**
 * Find the most-constrained unknown cell (row/col with fewest unknowns).
 */
function findBranchCell(
  grid: CellValue[][],
  rows: number,
  cols: number
): { row: number; col: number } | null {
  let bestRow = -1;
  let bestCol = -1;
  let bestScore = Infinity;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (grid[r][c] !== CellValue.Unknown) continue;

      // Score = unknowns in row + unknowns in col (lower = more constrained)
      let score = 0;
      for (let cc = 0; cc < cols; cc++) {
        if (grid[r][cc] === CellValue.Unknown) score++;
      }
      for (let rr = 0; rr < rows; rr++) {
        if (grid[rr][c] === CellValue.Unknown) score++;
      }

      if (score < bestScore) {
        bestScore = score;
        bestRow = r;
        bestCol = c;
      }
    }
  }

  return bestRow >= 0 ? { row: bestRow, col: bestCol } : null;
}

function backtrack(
  grid: CellValue[][],
  clues: { rows: number[][]; cols: number[][] },
  rows: number,
  cols: number,
  steps: SolveStep[],
  techniquesUsed: Set<Technique>
): boolean {
  const cell = findBranchCell(grid, rows, cols);
  if (!cell) return true; // No unknowns left

  for (const tryValue of [CellValue.Filled, CellValue.Empty] as const) {
    const savedGrid = cloneGrid(grid);
    const savedStepCount = steps.length;

    grid[cell.row][cell.col] = tryValue;
    steps.push({
      technique: 'backtracking',
      lineType: 'row',
      lineIndex: cell.row,
      cellsDeduced: [{ row: cell.row, col: cell.col, value: tryValue }],
    });

    const result = propagate(grid, clues, rows, cols, steps, techniquesUsed);

    if (result === 'solved') return true;

    if (result === 'stuck') {
      if (backtrack(grid, clues, rows, cols, steps, techniquesUsed)) {
        return true;
      }
    }

    // Restore grid
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        grid[r][c] = savedGrid[r][c];
      }
    }
    steps.length = savedStepCount;
  }

  return false;
}

function countSolutionsBacktrack(
  grid: CellValue[][],
  clues: { rows: number[][]; cols: number[][] },
  rows: number,
  cols: number,
  search: SolutionSearchState,
): number {
  if (search.aborted) return 0;

  const cell = findBranchCell(grid, rows, cols);
  if (!cell) return 1;

  let count = 0;

  for (const tryValue of [CellValue.Filled, CellValue.Empty] as const) {
    if (search.aborted || count >= search.limit) break;
    if (!consumeSearchState(search)) break;

    const branchGrid = cloneGrid(grid);
    branchGrid[cell.row][cell.col] = tryValue;

    const branchSteps: SolveStep[] = [];
    const techniques = new Set<Technique>();

    const result = propagate(
      branchGrid,
      clues,
      rows,
      cols,
      branchSteps,
      techniques
    );

    if (result === 'solved') {
      count++;
      if (count >= search.limit) return count;
    } else if (result === 'stuck') {
      count += countSolutionsBacktrack(branchGrid, clues, rows, cols, search);
      if (count >= search.limit) return count;
    }
    // contradiction: skip
  }

  return count;
}

interface SolutionSearchState {
  limit: number;
  maxStates: number;
  exploredStates: number;
  aborted: boolean;
}

function consumeSearchState(search: SolutionSearchState): boolean {
  if (search.exploredStates >= search.maxStates) {
    search.aborted = true;
    return false;
  }

  search.exploredStates++;
  return true;
}
